import { MyRouterContext } from '@/App';
import { MainContainer } from '@/components/MainContainer';
import { PageHeader } from '@/components/PageHeader';
import { Button, Center, Container, Group, Stack, Tabs, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { createFileRoute, LoaderFnContext, useRouter } from '@tanstack/react-router';
import { ActiveProjectList } from './-components/ActiveProjectList';
import { ArchivedProjectList } from './-components/ArchivedProjectList';
import { modals } from '@mantine/modals';
import { useForm } from 'react-hook-form';
import { notifyError, notifySuccess } from '@/lib/notification';
import { logger } from '@/lib/logger';
import { PendingInitialize } from '@/components/PendingInitialize';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

export const Route = createFileRoute('/_main/tenant/projects/')({
  loader,
  component: Component,
  pendingComponent: () => <PendingInitialize title="プロジェクト管理" />,
});

async function loader({
  context: { adminClient },
}: LoaderFnContext<Record<never, string>, never, MyRouterContext>) {
  const projects = await adminClient.admin_v1_get_all_projects();
  const archivedProjects = await adminClient.admin_v1_get_all_projects({
    queries: { status: 'archived' },
  });
  return {
    projects,
    archivedProjects,
    adminClient,
  };
}

function Component() {
  const { projects, archivedProjects, adminClient } = Route.useLoaderData();
  const router = useRouter();
  const client = useQueryClient();

  const createProject = useMutation({
    mutationFn: (name: string) =>
      adminClient.admin_v1_create_project({
        name: name,
        status: 'active',
      }),
    onSuccess: async (_, name, _ctx) => {
      client.invalidateQueries({ queryKey: ['projects'] });
      await router.load();
      notifySuccess(`プロジェクト ${name} を作成しました。`);
      modals.closeAll();
    },
    onError: (error, name, _ctx) => {
      logger.error(error);
      notifyError(`プロジェクト ${name} の作成に失敗しました。`);
    },
  });

  const openCreateProjectModal = () => {
    modals.open({
      size: 'md',
      title: 'プロジェクト新規作成',
      children: <CreateProjectForm onSubmit={({ name }) => createProject.mutate(name)} />,
    });
  };

  return (
    <MainContainer>
      <PageHeader
        title="プロジェクト管理"
        description={
          <>
            プロジェクトを使用することで、プロジェクト単位での API
            利用額の確認や上限設定、権限管理が可能です。
            <br />
            チーム・サービスごとに API
            利用額を分計したい場合には、プロジェクトを作成してご利用ください。
            <br />
            プロジェクトが不要な場合は、 Default プロジェクトが利用できます。
          </>
        }
      />
      <Group justify="end">
        <Button
          color="blue"
          size="sm"
          leftSection={<IconPlus style={{ width: '70%', height: '70%' }} />}
          onClick={openCreateProjectModal}
        >
          プロジェクトを作成
        </Button>
      </Group>
      <Tabs defaultValue="users" variant="outline">
        <Tabs.List>
          <Tabs.Tab value="users">アクティブ</Tabs.Tab>
          <Tabs.Tab value="invitations">アーカイブ</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="users" className="py-5">
          <ActiveProjectList projects={projects.items} adminClient={adminClient} />
        </Tabs.Panel>
        <Tabs.Panel value="invitations" className="py-5">
          <ArchivedProjectList projects={archivedProjects.items} />
        </Tabs.Panel>
      </Tabs>
    </MainContainer>
  );
}

const inputSchema = z.object({
  name: z.string().min(1, '必須項目です。').max(63, '63文字以内で入力してください。'),
});
type Inputs = z.infer<typeof inputSchema>;

type FormProps = {
  onSubmit: (data: Inputs) => void;
};

function CreateProjectForm({ onSubmit }: FormProps) {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<Inputs>({
    resolver: zodResolver(inputSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
    },
  });

  return (
    <Container m="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Center>
          <Stack w="100%">
            <TextInput
              label="プロジェクト名"
              placeholder="プロジェクト名"
              {...register('name')}
              error={errors.name?.message}
            />
            <Button type="submit" size="sm" disabled={!isValid} mt="lg">
              作成
            </Button>
          </Stack>
        </Center>
      </form>
    </Container>
  );
}
